import { useEffect, useState } from "react";
import type { AvatarSummary } from "../api/avatar.api";
import { AvatarArt } from "./AvatarArt";

interface UserAvatarProps {
  avatar?: AvatarSummary | null;
  name?: string | null;
  /** Diameter in px. */
  size?: number;
  className?: string;
}

const initialOf = (name?: string | null) => {
  const first = Array.from((name ?? "").trim())[0];
  return first ? first.toUpperCase() : "?";
};

/** Active photo or character → initials (also on photo error). No fetching or history required. */
export function UserAvatar({ avatar, name, size = 40, className = "" }: UserAvatarProps) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const image = avatar?.mode === "photo" ? avatar.photo?.url ?? null : null;
  useEffect(() => setPhotoFailed(false), [image]);

  const label = name?.trim() || "รูปโปรไฟล์";
  let content: React.ReactNode;
  if (image && !photoFailed) {
    content = (
      <img
        src={image}
        alt=""
        width={size}
        height={size}
        className="w-full h-full object-cover"
        onError={() => setPhotoFailed(true)}
        draggable={false}
      />
    );
  } else if (avatar?.mode === "character" && avatar.config) {
    content = <AvatarArt config={avatar.config} size={size} />;
  } else {
    content = (
      <span aria-hidden="true" style={{ fontSize: Math.max(12, Math.round(size * 0.42)) }}>
        {initialOf(name)}
      </span>
    );
  }

  return (
    <span
      role="img"
      aria-label={label}
      className={`user-avatar inline-flex items-center justify-center shrink-0 rounded-full overflow-hidden bg-ink-100 text-ink-700 font-display font-black leading-none select-none ${className}`}
      style={{ width: size, height: size }}
    >
      {content}
    </span>
  );
}

export default UserAvatar;
